import {
  Box,
  Button,
  TextField,
  IconButton,
  Collapse,
  Typography,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import { useState, useEffect } from "react";
import { EmailAnnotation } from "@services/documents/email/types";
import { apiClient } from "../../utils/apiClient";

interface EmailAnnotationComponentProps {
  messageId: string;
}

export const EmailAnnotationComponent: React.FC<
  EmailAnnotationComponentProps
> = ({ messageId }) => {
  const [annotation, setAnnotation] = useState<EmailAnnotation | null>(null);
  const [isAnnotating, setIsAnnotating] = useState(false);
  const [tags, setTags] = useState<string>("");
  const [annotationText, setAnnotationText] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  // Load existing annotation
  useEffect(() => {
    const loadAnnotation = async () => {
      try {
        const response = await apiClient.get<{
          success: boolean;
          annotation?: EmailAnnotation;
          error?: string;
        }>(`/documents/email/annotation/${messageId}`);

        if (response.success && response.annotation) {
          setAnnotation(response.annotation);
          setTags(response.annotation.tags.join(", "));
          setAnnotationText(response.annotation.annotation);
        }
      } catch (error) {
        console.error("Failed to load annotation:", error);
      }
    };

    loadAnnotation();
  }, [messageId]);

  const handleSaveAnnotation = async () => {
    setLoading(true);
    setMessage(null);
    try {
      const response = await apiClient.post<{
        success: boolean;
        annotationId?: string;
        error?: string;
      }>("/documents/email/annotation", {
        messageId,
        tags: tags
          .split(",")
          .map((tag) => tag.trim())
          .filter((tag) => tag.length > 0),
        annotation: annotationText,
      });

      if (response.success) {
        // Reload annotation
        const getResponse = await apiClient.get<{
          success: boolean;
          annotation?: EmailAnnotation;
          error?: string;
        }>(`/documents/email/annotation/${messageId}`);

        if (getResponse.success && getResponse.annotation) {
          setAnnotation(getResponse.annotation);
        }
        setIsAnnotating(false);
        setMessage({ type: "success", text: "Annotation saved successfully!" });
      } else {
        setMessage({
          type: "error",
          text: response.error || "Failed to save annotation",
        });
      }
    } catch (error: any) {
      console.error("Failed to save annotation:", error);
      setMessage({
        type: "error",
        text: error.message || "Network error: Unable to save annotation",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAnnotation = async () => {
    setLoading(true);
    setMessage(null);
    try {
      const response = await apiClient.delete<{
        success: boolean;
        error?: string;
      }>(`/documents/email/annotation/${messageId}`);

      if (response.success) {
        setAnnotation(null);
        setTags("");
        setAnnotationText("");
        setIsAnnotating(false);
        setMessage({
          type: "success",
          text: "Annotation deleted successfully!",
        });
      } else {
        setMessage({
          type: "error",
          text: response.error || "Failed to delete annotation",
        });
      }
    } catch (error: any) {
      console.error("Failed to delete annotation:", error);
      setMessage({
        type: "error",
        text: error.message || "Network error: Unable to delete annotation",
      });
    } finally {
      setLoading(false);
    }
  };

  const startAnnotating = () => {
    setIsAnnotating(true);
  };

  const cancelAnnotating = () => {
    if (annotation) {
      setTags(annotation.tags.join(", "));
      setAnnotationText(annotation.annotation);
    } else {
      setTags("");
      setAnnotationText("");
    }
    setIsAnnotating(false);
    setMessage(null);
  };

  return (
    <Box sx={{ mb: 2 }}>
      {/* Show existing annotation when not editing */}
      {annotation && !isAnnotating && (
        <Box
          sx={{
            p: 2,
            backgroundColor: "#f0f8ff",
            borderRadius: 1,
            border: "2px solid #2196f3",
            position: "relative",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography variant="body2" fontWeight="bold" color="primary">
              Annotation:
            </Typography>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={startAnnotating}
                disabled={loading}
                title="Edit annotation"
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleDeleteAnnotation}
                disabled={loading}
                title="Delete annotation"
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {annotation.tags.length > 0 && (
            <Box sx={{ mb: 1 }}>
              {annotation.tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  sx={{ mr: 0.5, mb: 0.5 }}
                />
              ))}
            </Box>
          )}
          {annotation.annotation && (
            <Typography variant="body2" color="textSecondary">
              {annotation.annotation}
            </Typography>
          )}
        </Box>
      )}

      {/* Show annotate button when no annotation exists */}
      {/* {!annotation && !isAnnotating && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<EditIcon />}
          onClick={startAnnotating}
          disabled={loading}
          sx={{ mb: 1 }}
        >
          Annotate
        </Button>
      )} */}

      {/* Annotation edit form */}
      <Collapse in={isAnnotating}>
        <Box sx={{ p: 2, backgroundColor: "#f8f9fa", borderRadius: 1 }}>
          <TextField
            fullWidth
            size="small"
            label="Tags (comma-separated)"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="urgent, customer, billing"
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            size="small"
            label="Annotation"
            value={annotationText}
            onChange={(e) => setAnnotationText(e.target.value)}
            placeholder="Add your notes about this email..."
            multiline
            rows={3}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={handleSaveAnnotation}
              disabled={loading}
              startIcon={
                loading ? <CircularProgress size={16} /> : <SaveIcon />
              }
            >
              Save
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={cancelAnnotating}
              disabled={loading}
            >
              Cancel
            </Button>
          </Box>
          {message && (
            <Alert
              severity={message.type}
              sx={{ mt: 2 }}
              onClose={() => setMessage(null)}
            >
              {message.text}
            </Alert>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};
